/**
 * @file ログインページ
 *
 * ユーザーID・パスワードでのログイン処理を担当する。
 * ログイン成功時は契約情報・プラン情報・ユーザー情報を並列取得し、
 * メール未登録であればメール登録ページへ、それ以外はトップへ遷移する。
 * redirect_uriパラメータがあれば指定先へリダイレクトする。
 * ログイン連続失敗時はアカウントロックメッセージを表示する。
 */
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';
import { NextPage } from 'next/types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import * as yup from 'yup';

import api from '@/utils/api';
import { setCredentials, setSessionStartTime } from '@/utils/cognito';
import { formatDate, formatZipCode } from '@/utils/commonFunc';
import { ApiUrl, Constants } from '@/utils/constants';

import { alertState } from '@/states/alertState';
import { apiStatusState } from '@/states/apiStatusState';
import { customerInfoState } from '@/states/customerInfoState';
import { LoginFromType, loginFromState } from '@/states/loginFromState';
import { loginInfoState } from '@/states/loginInfoState';
import { planInfoState } from '@/states/planInfoState';
import { providerInfoState } from '@/states/providerInfoState';
import { LoginResponse } from '@/types/api/mypage/login';

import useGetContractInfo from '@/hooks/api/useGetContractInfo';
import useGetPlanInfo from '@/hooks/api/useGetPlanInfo';
import useGetUserInfo from '@/hooks/api/useGetUserInfo';
import useSetLoading from '@/hooks/useSetLoading';

import LinkButton from '@/components/atoms/LinkButton';
import SystemErrorMessage from '@/components/atoms/SystemErrorMessage';
import MlLabeledTextBox from '@/components/atoms/molecules/MlLabeledTextBox';

const Login: NextPage = () => {
  const router = useRouter();
  const { query } = router;
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);
  const setLoginInfo = useSetRecoilState(loginInfoState);
  const [localErrorMessage, sendLocalErrorMessage] = useState('');
  const loginFrom = useRecoilValue(loginFromState);
  const { execute: fetchUserInfo } = useGetUserInfo();
  const setCustomerInfo = useSetRecoilState(customerInfoState);
  const { execute: fetchContractInfo } = useGetContractInfo();
  const setPlanInfo = useSetRecoilState(planInfoState);
  const { execute: fetchPlanInfo } = useGetPlanInfo();
  const providerInfo = useRecoilValue(providerInfoState);
  const [apiStatus, setApiStatus] = useRecoilState(apiStatusState);

  // ログインID: 先頭が英字、英数字・ハイフン・アンダーバー・ドットで6〜32文字（連続ドット不可）
  // パスワード: 先頭が英数字、英字と数字を各1文字以上含む8文字以上（ハイフン・アンダーバー可）
  const schema = yup.object().shape({
    userId: yup
      .string()
      .required('ログインIDを入力してください。')
      .matches(/^(?=.*[a-zA-Z])(?!.*[.]{2,})[a-zA-Z][0-9a-zA-Z-_.]{4,30}[0-9a-zA-Z-_]$/, 'ログインIDの入力が正しくありません。'),
    password: yup
      .string()
      .required('パスワードを入力してください。')
      .matches(/^(?=.*[0-9])(?=.*[a-zA-Z])[0-9a-zA-Z][0-9a-zA-Z-_]{7,1023}$/, 'パスワードの入力が正しくありません。'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFromType>({
    defaultValues: loginFrom,
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // 顧客情報取得
  const callAPIGetContractInfo = async () => {
    try {
      startLoading(ApiUrl.Read.CONTRACT_INFO);
      const { data } = await fetchContractInfo();
      setCustomerInfo({
        isp_cn_no: data.isp_contract_no,
        last_name: data.last_name,
        first_name: data.first_name,
        last_name_kana: data.last_name_kana,
        first_name_kana: data.first_name_kana,
        birthday: data.birthday,
        address_zip_code: formatZipCode(data.zip_code),
        address_prefecture: data.prefecture,
        address_city: data.city,
        address_town: data.town,
        address_optional: data.address_optional,
        tel: data.tel,
        tel_daytime: data.tel_daytime,
        mail: data.email,
      });
      return { data, isError: false };
    } catch (error) {
      return { isError: true };
    } finally {
      finishLoading(ApiUrl.Read.CONTRACT_INFO);
    }
  };

  // プラン情報取得
  const callAPIGetPlanInfo = async () => {
    try {
      startLoading(ApiUrl.Read.PLAN_INFO);
      const { data } = await fetchPlanInfo();
      setPlanInfo({
        planCode: data.plan_code,
        planName: data.plan_name,
        cnStatusCode: data.cn_status_code,
        cnStatusName: data.cn_status_name,
        planType: data.plan_type,
        serviceType: data.service_type,
        serviceStartDate: formatDate(data.service_start_date),
        acceptDate: formatDate(data.accept_date),
        contractTotalMonth: data.contract_total_month,
        preferredQuitDate: data.preferred_quit_date,
        canEntryCancel: data.can_entry_cancel,
        canQuitCancel: data.can_quit_cancel,
      });
      return { data, isError: false };
    } catch (error) {
      return { isError: true };
    } finally {
      finishLoading(ApiUrl.Read.PLAN_INFO);
    }
  };

  // ユーザー情報取得
  const callAPIGetUserInfo = async () => {
    try {
      startLoading(ApiUrl.Read.USER_INFO);
      const { data } = await fetchUserInfo();
      setLoginInfo({ userId: data.user_id, is_verified_email: data.is_verified_email });
      return { data, isError: false };
    } catch (error) {
      return { isError: true };
    } finally {
      finishLoading(ApiUrl.Read.USER_INFO);
    }
  };

  // 非同期通信
  // ログイン成功後に契約・プラン・ユーザー情報を並列取得し、結果に応じてリダイレクト先を決定する
  // 優先順位: メール未登録→メール登録画面 / redirect_uriあり→指定先 / それ以外→トップ
  const callAPI = async () => {
    // 3つのAPIを並列実行してレスポンス時間を短縮する
    const result = await Promise.all([callAPIGetContractInfo(), callAPIGetPlanInfo(), callAPIGetUserInfo()]);

    if (result.some((r) => r.isError)) {
      setAlert({
        isOpen: true,
        apiType: '',
      });
      return;
    }

    // メールアドレスが登録されてない場合はメールアドレス登録画面に遷移
    if (!result[0].data?.email) {
      router.push('/mail');
      return;
    }

    // 上記以外の場合で、リダイレクトする画面が設定されている場合
    if (query.redirect_uri && typeof query.redirect_uri === 'string') {
      // オープンリダイレクト防止: パスが'/'で始まる相対パスのみ許可（外部URLへの遷移を防ぐ）
      if (router.asPath.split('redirect_uri=')[1].startsWith('/')) {
        router.push(router.asPath.split('redirect_uri=')[1]);
        return;
      }
      // 不正なredirect_uriの場合は安全にトップへフォールバック
      router.push('/top');
      return;
    }

    // 上記すべてに該当しない場合はトップ画面に遷移
    router.push('/top');
  };

  // ログイン処理
  // フロー: セッションクリア → ログインAPI(POST) → Credential保存 → 並列情報取得 → リダイレクト
  const handleClickLoginButton = async (from: LoginFromType) => {
    // 前回セッションの残データを消去して新規ログインに備える
    sessionStorage.clear();
    // 認証前のためNonProtected（認証不要）エンドポイントを使用
    startLoading(ApiUrl.NonProtected.LOGIN);
    await api
      .post(
        ApiUrl.NonProtected.LOGIN,
        {
          user_id: from.userId,
          password: from.password,
          provider_id: providerInfo.providerId,
        },
        { headers: { 'content-type': 'application/json' } }
      )
      .then((res: AxiosResponse<LoginResponse>) => {
        sendLocalErrorMessage('');
        // Cognito Credentialをローカルに保存（以降のAPI呼び出しで署名に使用）
        setCredentials(res.data);
        // セッションタイムアウト（30分）の計測開始
        setSessionStartTime();
        callAPI();
        setApiStatus({
          ...apiStatus,
          caution: true,
          customer: false,
          autoNotification: true,
          user: false,
          payment: true,
          options: true,
          billing: true,
          agreement: true,
          fvnoProvide: true,
          campaigin: true,
          rlsRefundList: true,
        });
      })
      .catch((error) => {
        if (error.response.data?.result_message === 'user is locked out') {
          sendLocalErrorMessage('ログインが連続して失敗したため、認証をロックしました。\n時間をおいてから再度お試しください。');
        } else {
          sendLocalErrorMessage('ログインIDまたはパスワードを正しく入力して下さい');
        }
      })
      .finally(() => finishLoading(ApiUrl.NonProtected.LOGIN));
  };

  // 出力テキスト判別
  const judgeText = () => {
    if (providerInfo.providerId === Constants.ProviderId.SNCC) {
      return (
        <>
          ※ログインID・パスワードは「契約内容通知書」に記載しています。
          <br />
          ※NURO 光 各サービスをご利用中のお客さまは{' '}
          <a href='https://www.nuro.jp/app/mypage/' rel='noreferrer'>
            NURO 光 マイページ
          </a>
          をご利用ください
          <br />
          ※オプションサービスの申込をご希望のお客さまで、
          <br />
          マイページをお持ちでないお客さまは
          <a href='https://nuro.jp/connect/user/olsu/mansion/olsu#!/' rel='noreferrer'>
            こちら
          </a>
          からお申し込みください
        </>
      );
    }
    return <>※ログインID・パスワードは「お申込内容のご案内」に記載しています。</>;
  };

  return (
    <main>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <div className='form-area'>
          {localErrorMessage && <SystemErrorMessage message={localErrorMessage} />}

          <dl>
            <MlLabeledTextBox type='text' id='userId' register={register('userId')} error={errors.userId}>
              ログインID
              <br />
              <span>(半角英数字)</span>
            </MlLabeledTextBox>

            <MlLabeledTextBox type='password' id='password' register={register('password')} error={errors.password} autoComplete='off'>
              パスワード
              <br />
              <span>(半角英数字)</span>
            </MlLabeledTextBox>
          </dl>

          <div className='announcement-label'>{judgeText()}</div>

          <LinkButton onClick={handleSubmit(handleClickLoginButton)}>ログイン</LinkButton>

          <div className='announcement-label' style={{ paddingTop: '16px' }}>
            ※ユーザーIDパスワード再設定機能は一時的にご利用いただけません。
          </div>
        </div>
      </form>
    </main>
  );
};
export default Login;
